/**
 * Design spec §1.2 — `types/`: plain row and entity types. **No logic, no imports.**
 *
 * This is the layer every other layer may import, which is what lets `archive/` serialize rows
 * without reaching into `domain/` or `data/`. It has almost no runtime footprint: every export is a
 * type, or a frozen `const` of string literals that exists so a value can be *named* rather than
 * spelled bare at a call site (see `ARTIFACT_PHASE`).
 *
 * Build unit U-0 declared the two entities the measured transaction touches (`Observation`,
 * `Hypothesis`, per design §3.1) plus the log entry (OBS-1) and the harness's own result rows;
 * **U-3 declares the rest of the product schema**, below the U-3 banner.
 *
 * The one entity that is NOT here is `AudioBlob`. Design §3.1's boundary rule: `domain/` is DOM-free
 * and imports `types/`, so `types/` inherits the constraint and is compiled without DOM libs. A
 * `Blob`-typed field here would either fail `tsconfig.pure.json` or force `types/` to be widened —
 * and widening is the plausible wrong answer, because it makes the error go away while silently
 * disabling the boundary NFR-6 makes build-failing. `AudioBlobRow` lives in `data/blobStore.ts`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Domain enums (design §3.1, requirements §6)
// ─────────────────────────────────────────────────────────────────────────────

export type ObservationKind = 'increment' | 'voiceNote' | 'flag';

/**
 * Requirements §6 / design §2.1. The nine-value lifecycle of a match.
 *
 * The two normative §2 groupings — **in-play** (`firstHalf`, `secondHalf`) and **in-match** (those
 * two plus `halftime`) — are declared once, in `domain/machine.ts`, together with the narrowed
 * types and the guards. They are deliberately NOT here: `types/` is imported by every layer, and a
 * grouping that anyone can reach is a grouping anyone can hand-roll a fourth version of. The
 * `no-restricted-syntax` rule in `eslint.config.js` backs that up by refusing a bare `MatchState`
 * string literal anywhere outside `src/domain/`.
 */
export type MatchState =
  | 'idle'
  | 'preMatch'
  | 'firstHalf'
  | 'halftime'
  | 'secondHalf'
  | 'fullTime'
  | 'triage'
  | 'distill'
  | 'archived';

/** Requirements §6 — `Match.capturePhase`. Moves `discovery → locked` once, and never back. */
export type CapturePhase = 'discovery' | 'locked';

/**
 * The wall-clock anchors every match minute derives from (INV-7), plus the half length they are
 * measured against (SES-4, SES-5).
 *
 * A slice of the `Match` row rather than a domain invention: `domain/clock.ts` is the only reader
 * and `domain/machine.ts` is the only writer, so the pair is the whole temporal contract.
 */
export interface MatchAnchors {
  halfLengthMin: number;
  kickoffTs: number | null;
  halfTs: number | null;
  kickoff2Ts: number | null;
  fullTimeTs: number | null;
}

/**
 * SES-8 — one row's re-derived minute, and **the only field a re-derivation may change** (INV-1).
 *
 * Declared here rather than beside either repo because three layers now need the same shape and none
 * of them may import another's: `domain/` computes the minutes (`clock.rederiveMatchMinute`),
 * `domain/commands` carries them on a `RowOp`, and `data/repos/{observations,matchNotes}` writes
 * them. One declaration is what stops the observation half and the note half of a single adjustment
 * from drifting apart — which is the reason `matchNotes.ts` already imported it from `observations`
 * rather than declaring a second copy.
 *
 * The narrowness *is* the mechanism: with only `{id, matchMinute}` in scope, no caller can carry a
 * stale `kind` or `createdAt` through a spread, so "changes `matchMinute` and nothing else" is a
 * property of the type rather than of a repo remembering to ignore the rest.
 */
export interface MinuteRederivation {
  id: string;
  matchMinute: number;
}

export type HypothesisPhase =
  | 'inPossession'
  | 'outOfPossession'
  | 'attTransition'
  | 'defTransition'
  | 'setPiece'
  | 'individual'
  | 'gk';

/** DISC-2's four pages. Non-optional on `Hypothesis` per requirements revision 2. */
export type DiscoveryBucket =
  | 'inPossession'
  | 'outOfPossession'
  | 'attTransition'
  | 'defTransition';

/**
 * The dismissal dimension (design §3.3). User-set, reversible, rare.
 * `null` means "not dismissed" — it is not the same thing as `tracking`.
 */
export type StatusOverride = null | 'disproved' | 'invalidated';

/** The four-value presentation the contract names. Derived; see `domain/status.ts`. */
export type Status = 'tracking' | 'confirmed' | 'disproved' | 'invalidated';

// ─────────────────────────────────────────────────────────────────────────────
// Row shapes — the two tables the measured `readwrite` spans (design §5.1 step 7)
// ─────────────────────────────────────────────────────────────────────────────

export interface ObservationRow {
  id: string;
  hypothesisId: string | null;
  matchId: string;
  matchMinute: number;
  kind: ObservationKind;
  /** Design §3.2 — replaces `audioBlob`/`audioMime`; the blob lands later under a pre-minted id. */
  audioBlobId: string | null;
  createdAt: number;
}

export interface HypothesisRow {
  id: string;
  matchId: string;
  text: string;
  phase: HypothesisPhase;
  discoveryBucket: DiscoveryBucket;
  playerId: string | null;
  statusOverride: StatusOverride;
  cachedCount: number;
  /** Design §3.2 — makes the LIVE-6 undo window survive a process kill with no timer. */
  lastIncrementTs: number | null;
  /**
   * Design §3.2 / §6.1 — **DISC-4's third sort term**, added by `v002`.
   *
   * `discoveryOrder` sorts by `(cachedCount desc, firstObservationCreatedAt asc, createdAt asc,
   * id asc)`. Before kickoff every row ties on the first two terms, so without this the whole board
   * falls through to term 4 — and ids are `crypto.randomUUID()` (§11.7), so the board came up in
   * *random* order at the whistle. That is what this column exists to fix; it is not bookkeeping.
   *
   * **`0` is the migration's backfill and means "predates the column"** (§13.3). Rows carrying it
   * tie at term 3 and fall through to `id`, which is exactly how they sorted before `v002` — the
   * migration is behaviour-preserving by construction rather than by inventing a creation time the
   * database never held.
   *
   * INV-7: this is a **tie-break**, one of the four allowlisted `createdAt` comparisons (§4). It may
   * not become a primary temporal key — `matchMinute` is the only temporal field a domain derivation
   * reads, and §18.2's V1 test holds that line by perturbing every `createdAt` in a fixture with no
   * ties and asserting nothing moves.
   */
  createdAt: number;
  /** Materialized, written by exactly one expression at one call site (design §3.3). */
  status: Status;
  boardPosition: number | null;
  sourceTemplateId: string | null;
  carriedFromMatchId: string | null;
  carriedStatus: Status | null;
  carriedCount: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build unit U-3 — the rest of the product schema (design §3.1, requirements §6)
//
// Every row here is plain data: strings, numbers, booleans, string-union enums and plain JSON
// structures. **`AudioBlob` is deliberately absent** — it is the one entity carrying a lib type
// (`Blob`), and design §3.1's boundary rule puts it in `data/`. See the note further down.
// ─────────────────────────────────────────────────────────────────────────────

/** Requirements §6 — `Player.foot`. There is no name field, per ENV-11. */
export type Foot = 'left' | 'right';

export interface TeamRow {
  id: string;
  name: string;
  club: string | null;
  ageGroup: string | null;
  notes: string | null;
  createdAt: number;
}

export interface PlayerRow {
  id: string;
  teamId: string;
  /** Shirt number. Unique per team via `&[teamId+number]`, which is what backs the PRE-4 upsert. */
  number: number;
  foot: Foot | null;
  notes: string | null;
}

// ── Build unit U-23 — PRE-8, the match format ────────────────────────────────

/**
 * PRE-8 — the three formats youth soccer is actually played in.
 *
 * A closed union over an entity's own field, declared here for the same reason `ARTIFACT_PHASE` and
 * `SETTING_KEYS` are: `archive/import.ts` needs the `'11v11'` literal for §11.7a's parse-stage
 * backfill and **may not import `domain/`** (§1.2), so this placement is what lets it name the value
 * against a checked union instead of spelling an unchecked string.
 *
 * The **shape lists** that belong to each format, the field-player counts, and the arithmetic that
 * ties them together are `domain/setup.ts`'s — design §6.0a. Nothing about how a format is *played*
 * lives in `types/`.
 */
export type MatchFormat = '7v7' | '9v9' | '11v11';

/**
 * The three formats as values, in ascending size — the order the Pre-Match control renders them in.
 *
 * `satisfies` proves every listed value is a `MatchFormat`; `data/rowSchemas.ts` parses against this
 * list, so the repo boundary and the union cannot drift.
 */
export const MATCH_FORMATS = ['7v7', '9v9', '11v11'] as const satisfies readonly MatchFormat[];

// ── Build unit B-1 — the match purpose (design spec §4.1, flag F-2 approved) ─

/**
 * B §4.1 / F-2 — what the match is *for*: our own team's match, or an advance scout of a
 * future opponent. The halftime workflow forks on it (scenario 20), and the card framing
 * derives from it (§2.5).
 *
 * Declared here for the same reason `MATCH_FORMATS` is: `archive/import.ts` needs the
 * `'teamMatch'` literal for the parse-stage backfill of pre-purpose archives (§4.4) and may
 * not import `domain/`, so this placement lets it name the value against a checked union.
 */
export type MatchPurpose = 'teamMatch' | 'advanceScout';

/**
 * The two purposes as values, in the order the setup screen renders them (§2.4 step 3).
 * `data/rowSchemas.ts` parses against this list, so the repo boundary and the union cannot drift.
 */
export const MATCH_PURPOSES = ['teamMatch', 'advanceScout'] as const satisfies readonly MatchPurpose[];

export interface MatchRow {
  id: string;
  teamId: string;
  comp: string | null;
  /**
   * Calendar date as `YYYY-MM-DD`, **not** a timestamp.
   *
   * §11.7 requires integer milliseconds for all *times*; a match date is a date, and storing local
   * midnight as an epoch would make the same match read as a different day after a timezone change.
   * The fixed-width form also sorts lexicographically, which is what FILE-3's history ordering wants.
   */
  date: string;
  /**
   * PRE-8 — the match format, added by requirements revision 3 (F-40).
   *
   * **Nullable in storage and required to leave Pre-Match**, and the two are not in tension (§6.0a).
   * PRE-8 gives the format no default and no pre-selected value, so `CreateMatch` — which fires the
   * moment an opponent is chosen, before the user has been asked anything — has nothing to write but
   * `null`. `StartCapture` is the single clamp: both PRE-7 exits route through it and it refuses
   * while this is `null`.
   *
   * **Field order is normative** (§11.7a): `format` sits immediately after `date` here, in
   * `MatchSchema`, and in `archive/fields.ts`'s `MATCH_FIELDS`. Any position would produce valid
   * bytes; disagreement between the three would not.
   */
  format: MatchFormat | null;
  /**
   * B §4.1 (F-2, approved) — the match purpose, added by migration `v004` with existing rows
   * backfilled **`'teamMatch'`** (every A match ran the team-facing workflow).
   *
   * **Nullable in storage and required by B's setup UI, and the two are not in tension** — the
   * `format` precedent one field up, exactly: the purpose has no default and no pre-selected
   * value, so `CreateMatch` — which fires the moment an opponent is chosen — has nothing to
   * write but `null`. The required-choice gate is B's UI (`canBegin`), ruled **UI-level** by
   * F-2; the engine deliberately does not gate `StartCapture` on it.
   *
   * **Field order is normative**: `purpose` sits immediately after `format` here, in
   * `MatchSchema`, and in `archive/fields.ts`'s `MATCH_FIELDS`, so the three never have to be
   * reconciled (the §11.7a discipline `format` established).
   */
  purpose: MatchPurpose | null;
  halfLengthMin: number;
  /**
   * PRE-1 — one of a fixed shape list, never a drawing. Added by requirements revision 2 (F-34).
   *
   * Which list is `format`'s business (§6.0a). A stored guess is never left un-offerable by its own
   * match's format: `MatchSchema` refuses the pairing at the repo boundary, and changing the format
   * clears a guess the new format cannot offer in the same transaction.
   */
  formationGuess: string | null;
  kickoffTs: number | null;
  halfTs: number | null;
  kickoff2Ts: number | null;
  fullTimeTs: number | null;
  state: MatchState;
  capturePhase: CapturePhase;
  lockTs: number | null;
  abandoned: boolean;
  lastSyncedTs: number | null;
  /** Design §11.5 refinement — without it SYN-6 reports a match with an unsynced second half as protected. */
  lastSyncedHash: string | null;
  /** DIST-1 — passing triage, possibly empty. Added by requirements revision 2 (F-2). */
  triagePassedTs: number | null;
}

// A `MatchRow` structurally satisfies `MatchAnchors` (`halfLengthMin` plus the four anchors), so
// `domain/clock.ts` takes the match row directly. There is no projection step that could drop or
// reorder an anchor, and `data/db.test.ts` pins that structural relationship with a type assignment.

/**
 * SP-6 — the kinds of set piece a capture can be.
 *
 * **This union is closed, and widening it is a code change, not a data change.** The comment that
 * stood here until B-23 said the field "stays open to more values", which was never true of
 * anything a compiler evaluates: `MARKER_CYCLE`, the two label maps and `rowSchemas`'s `z.enum` all
 * enumerate it exhaustively, so a new value is a compile error in each of them (which is the point —
 * the error is the checklist). B-23 added the two free-kick kinds by doing exactly that.
 *
 * Requirements §3.2.14 asks for *"a dedicated mode for corners/free kicks"*. All four ship in
 * **Touchline B**; A's sheet renders corners only (see `ui/capture/SetPieceScreen.tsx` — its
 * corners-only list is explicit there, not implied by this union).
 */
export type SetPieceType = 'defCorner' | 'attCorner' | 'defFreeKick' | 'attFreeKick';

/**
 * SP-2's marker cycle, plus SP-3's delivery target and optional screener, plus B-23's two
 * free-kick values.
 *
 * `kickPoint` is *where the ball is*. A corner has a fixed restart point and needs no marker for it;
 * a free kick's restart point is the single most important fact about it, so it is the first thing a
 * free-kick tap marks. `wall` is the defensive wall — markers, not a scheme, so `SetPieceScheme`
 * (which A also renders) is untouched by free kicks.
 */
export type SetPieceMarkerType =
  | 'zonal'
  | 'man'
  | 'nearPost'
  | 'edgeOfBox'
  | 'target'
  | 'screener'
  | 'wall'
  | 'kickPoint';

/** SP-5 — normalized 0–1 box coordinates, so the share card re-renders resolution-independently. */
export interface SetPieceMarker {
  x: number;
  y: number;
  type: SetPieceMarkerType;
}

/** SP-2 `scheme` segmented control. */
export type SetPieceScheme = 'zonal' | 'man' | 'hybrid';

/**
 * The per-type option set (SP-2 toggles, SP-3 swing and target zone).
 *
 * Kept as one record of optional keys rather than a discriminated union because requirements §6 names
 * a single `flags` field: a union keyed on `SetPieceType` would need a **stored-data migration** every
 * time a kind is added, which §3.5's reasoning about `markers` rejects for the same reason. B-23 is
 * the proof — adding two set-piece kinds touched no stored row and needed no migration, because every
 * key here is optional and free kicks add none.
 */
export interface SetPieceFlags {
  leftUpTop?: boolean;
  edgeMarked?: boolean;
  swing?: 'in' | 'out';
  targetZone?: 'near' | 'far' | 'edge';
}

export interface SetPieceRow {
  id: string;
  matchId: string;
  type: SetPieceType;
  matchMinute: number;
  scheme: SetPieceScheme | null;
  /** §3.5 — JSON, not rows. Never queried by individual marker; read whole to re-render. */
  markers: SetPieceMarker[];
  flags: SetPieceFlags;
  notes: string | null;
  /** SES-3 — resume into a half-finished capture. Cleared when the sheet is exited. */
  draft: boolean;
}

/**
 * `TalkPoint.phase` and `MatchNote.phase`.
 *
 * The two values collide with two `MatchState` names, and that is a collision, not an identity:
 * this enum says *when in the match the artifact was authored*, not what the match is doing now. It
 * is spelled through `ARTIFACT_PHASE` outside `types/` so the `ses-1/match-state-literals` lint rule
 * keeps its full strength — a bare `'fullTime'` in `data/` or `app/` stays an error.
 */
export type ArtifactPhase = 'halftime' | 'fullTime';

/** Named access to the two `ArtifactPhase` values. See the note on `ArtifactPhase`. */
export const ARTIFACT_PHASE = {
  halftime: 'halftime',
  fullTime: 'fullTime',
} as const satisfies Record<ArtifactPhase, ArtifactPhase>;

/**
 * INV-13 as a **discriminated union**: `instructionText` is non-optional on the `fullTime` variant.
 *
 * A `fullTime` TalkPoint without instruction text is therefore not constructible — the invariant is
 * a compile error rather than a validation rule someone remembers to run. The repo boundary adds the
 * runtime half for rows arriving from an import (design §4, INV-13).
 */
export interface FullTimeTalkPointRow {
  id: string;
  matchId: string;
  phase: 'fullTime';
  /** DIST-2 — derived from selection order and rewritten `1..n`; never "next free rank". */
  rank: number;
  instructionText: string;
  /** DIST-6 — so FILE-1 can show which tracked patterns became instructions. */
  sourceHypothesisId: string | null;
  /** DIST-4 — an attached SetPiece capture. */
  attachedSetPieceId: string | null;
}

/** INV-13 — "a halftime adjustment is usable with nothing but its source hypothesis text." */
export interface HalftimeTalkPointRow {
  id: string;
  matchId: string;
  phase: 'halftime';
  rank: number;
  instructionText: string | null;
  sourceHypothesisId: string | null;
  attachedSetPieceId: string | null;
}

export type TalkPointRow = FullTimeTalkPointRow | HalftimeTalkPointRow;

/**
 * One entry of an ordered talk-point selection, **without its rank** (§10.2, HT-5).
 *
 * Spelled as an explicit union of the two variants rather than `Omit<TalkPointRow, 'rank'>`: `Omit`
 * over a union collapses it to the intersection of its keys, which would quietly destroy the INV-13
 * discrimination this type exists to preserve.
 *
 * It lives **here** rather than beside `writeSelection` because `domain/commands` names it in the
 * `writeTalkPointSelection` op, and NFR-6 forbids `domain/` from importing an I/O layer. `types/` is
 * the vocabulary both layers already share, so one definition serves both; `repos/talkPoints`
 * re-exports it so its own callers are unaffected.
 */
export type TalkPointSelection =
  | Omit<FullTimeTalkPointRow, 'rank'>
  | Omit<HalftimeTalkPointRow, 'rank'>;

/**
 * INV-14 — **not** an `Observation`. Mutable, excluded from replay, included in archives and in the
 * INV-8 cascade. `replay`/`reconcile` take `Observation[]` only, so a note cannot reach a count.
 */
export interface MatchNoteRow {
  id: string;
  matchId: string;
  phase: ArtifactPhase;
  text: string | null;
  /** Same refinement as `Observation`: the bytes live in `AudioBlob`, never on this row. */
  audioBlobId: string | null;
  matchMinute: number;
  createdAt: number;
  updatedAt: number;
}

/** PRE-4 — a fill-in-the-blank slot on a template entry. Resolved by pickers, never a keyboard. */
export interface TemplateSlot {
  key: string;
  kind: 'shirtNumber' | 'foot';
}

/**
 * One seeded hypothesis text (design §6.0).
 *
 * Each entry carries its **own** `phase` and `discoveryBucket`; there is no top-level bucket on
 * `Template`, because one template may span buckets.
 */
export interface TemplateEntry {
  text: string;
  phase: HypothesisPhase;
  discoveryBucket: DiscoveryBucket;
  slots?: TemplateSlot[];
}

export interface TemplateRow {
  id: string;
  name: string;
  /** Optional grouping label. Entry-level `phase` is what actually classifies a hypothesis. */
  phase: HypothesisPhase | null;
  hypothesisTexts: TemplateEntry[];
  /** PRE-2 — seeded content loads with `builtin = true`. Not indexed: IndexedDB has no boolean key. */
  builtin: boolean;
}

/** DIST-1 — `FlagResolution.action`. Added by requirements revision 2 (F-2). */
export type FlagResolutionAction = 'promoted' | 'dismissed';

/**
 * DIST-1 — **append-only**, never a mutation of the flag `Observation`, which INV-1 forbids.
 *
 * `matchId` is reached through the flag Observation rather than duplicated (design §3.1): a second
 * copy could disagree with the first, and the unique `&observationId` index already makes
 * "a resolved flag MUST NOT reappear in the queue" enforceable rather than hoped for.
 */
export interface FlagResolutionRow {
  id: string;
  observationId: string;
  action: FlagResolutionAction;
  resolvedAt: number;
  promotedHypothesisId: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build unit B-25 — the team profile's vocabularies (B design spec §15.2)
//
// Requirements §3.7.36–37. Declared here for the same reason `MATCH_FORMATS` and
// `MATCH_PURPOSES` are: `data/rowSchemas.ts` and `archive/` both need these values and neither
// may import `domain/` (§1.2). Nothing about how a position is *played* lives in `types/`.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * §3.7.36 — the fifteen positions, **in the coach's own shorthand and his own order**, not the
 * textbook's.
 *
 * `CB` is the **middle of a back three**: the stakeholder's 3-3-2 plays `LB CB RB`, so reading
 * `CB` as a centre-half of a back four would mis-describe every one of his players. The order is
 * his — goalkeeper, back line, wing-backs, midfield spine, wide midfield, wide forwards, centre
 * forwards — and it is the order the position grids render in, so a grid re-ordering is a visible
 * edit here rather than a quiet one in a component.
 */
export type Position =
  | 'GK'
  | 'LB'
  | 'CB'
  | 'RB'
  | 'LWB'
  | 'RWB'
  | 'CDM'
  | 'CM'
  | 'CAM'
  | 'LM'
  | 'RM'
  | 'LW'
  | 'RW'
  | 'CF'
  | 'ST';

/** The fifteen positions as values, in §3.7.36's order. */
export const POSITIONS = [
  'GK',
  'LB',
  'CB',
  'RB',
  'LWB',
  'RWB',
  'CDM',
  'CM',
  'CAM',
  'LM',
  'RM',
  'LW',
  'RW',
  'CF',
  'ST',
] as const satisfies readonly Position[];

/**
 * §3.7.37 — the three ratings, and **there is deliberately no `average` and no `unrated`**.
 *
 * "Unrated" is not a rating; it is the absence of one, and `OurPlayerRow.ratings` represents it by
 * omitting the key. A fourth member here would give absence a second spelling, which is precisely
 * the confusion §3.7.37 forbids ("an unrated attribute means NOT ASSESSED — it does not mean
 * average").
 */
export type AttributeRating = 'great' | 'okay' | 'needsWork';

/** The three ratings as values, best to worst — the order the rating control renders them in. */
export const ATTRIBUTE_RATINGS = ['great', 'okay', 'needsWork'] as const satisfies
  readonly AttributeRating[];

/** §3.7.37's four phases of play, in the requirement's own order. */
export type AttributeGroup = 'onTheBall' | 'gettingForward' | 'defending' | 'headAndVoice';

/**
 * §3.7.37 — the nineteen rateable attributes, grouped **5 + 4 + 7 + 3**.
 *
 * **Two distinctions in this list must never be merged, and both are stakeholder rulings:**
 *
 *   - `scansBeforeReceiving` vs `findsSpaceToReceive` — *"scanning is perception; finding space to
 *     receive is tactical movement"*. A player can do either without the other, and they gate
 *     different instructions.
 *   - `runsInBehind` vs `recoversWhenBeaten` — pace split in two, because they gate **opposite**
 *     instructions: one lets you play over the top, the other lets you push your line up.
 *
 * They are separate union members rather than a note in a comment, so merging one is a **compile**
 * failure at `AttributesCoverTheUnion` rather than a quiet edit somebody notices in a match.
 */
export type AttributeKey =
  // on the ball (5)
  | 'keepsItUnderPressure'
  | 'beatsAPlayer'
  | 'passesShort'
  | 'switchesPlay'
  | 'deliversSetPieces'
  // getting forward without the ball (4)
  | 'runsInBehind'
  | 'findsSpaceToReceive'
  | 'finishes'
  | 'getsForwardAndBack'
  // defending (7)
  | 'winsDuels'
  | 'pressesAndHunts'
  | 'defendsOneVOne'
  | 'recoversWhenBeaten'
  | 'holdsPosition'
  | 'tracksRunners'
  | 'winsItInTheAir'
  // head and voice (3)
  | 'scansBeforeReceiving'
  | 'picksTheRightOption'
  | 'organisesTeammates';

/** One row of {@link ATTRIBUTES}. `label` is the requirement's own phrase, verbatim. */
export interface AttributeSpec {
  readonly key: AttributeKey;
  readonly group: AttributeGroup;
  /**
   * **§3.7.37's wording, character for character.** Not a paraphrase and not a UI caption: the
   * rating sheet's row label, the prose renderer's wording and the requirement's vocabulary are
   * ONE string, so a vocabulary edit is one edit and the model never reads a second-hand version
   * of the coach's words. `teamProfile.spec.test.ts` reads the requirement and compares.
   */
  readonly label: string;
}

/**
 * **One table, three readers** (§15.2) — the rating sheet, the prose renderer, and the archive's
 * determinism suite all take their order from here rather than from stored data. Two players rated
 * in a different order therefore produce identically ordered prose.
 */
export const ATTRIBUTES = [
  { key: 'keepsItUnderPressure', group: 'onTheBall', label: 'keeps it under pressure' },
  { key: 'beatsAPlayer', group: 'onTheBall', label: 'beats a player one-v-one' },
  { key: 'passesShort', group: 'onTheBall', label: 'passes accurately, short' },
  { key: 'switchesPlay', group: 'onTheBall', label: 'switches play / hits it long' },
  { key: 'deliversSetPieces', group: 'onTheBall', label: 'delivers set pieces and crosses' },

  { key: 'runsInBehind', group: 'gettingForward', label: 'runs in behind' },
  { key: 'findsSpaceToReceive', group: 'gettingForward', label: 'finds space to receive' },
  { key: 'finishes', group: 'gettingForward', label: 'finishes' },
  { key: 'getsForwardAndBack', group: 'gettingForward', label: 'gets forward and back repeatedly' },

  { key: 'winsDuels', group: 'defending', label: 'wins duels' },
  { key: 'pressesAndHunts', group: 'defending', label: 'presses and hunts the ball' },
  { key: 'defendsOneVOne', group: 'defending', label: 'defends one-v-one' },
  { key: 'recoversWhenBeaten', group: 'defending', label: 'recovers when beaten' },
  { key: 'holdsPosition', group: 'defending', label: "holds position, doesn't get dragged" },
  { key: 'tracksRunners', group: 'defending', label: 'tracks runners' },
  { key: 'winsItInTheAir', group: 'defending', label: 'wins it in the air' },

  { key: 'scansBeforeReceiving', group: 'headAndVoice', label: 'scans before receiving' },
  { key: 'picksTheRightOption', group: 'headAndVoice', label: 'picks the right option quickly' },
  { key: 'organisesTeammates', group: 'headAndVoice', label: 'organises teammates' },
] as const satisfies readonly AttributeSpec[];

/**
 * §3.7.38 — the team's preferred shape: **two shapes and the mechanism between them**, which
 * §3.7.38 states is *one* fact rather than three.
 *
 * `''` means "not recorded" for each of the three text fields. Free text, not a formation enum:
 * the stakeholder's in-possession shape is *"more like a 2-4-2 or a 2-3-1-2"* — a range — and his
 * transition carries a **condition** no notation can express (the left-back pushes into midfield
 * *if* nobody is waiting high to run in behind). `FORMATION_SHAPES` describes the **opponent's**
 * guessed formation on **one match** from a fixed list; that is a different owner and a different
 * lifetime, so it is offered as tap-chips and never as the storage type.
 *
 * **`updatedAt` lives INSIDE the value, and that is ruling R-3's doing.** `SettingRow` is
 * `{key, value}` with no timestamp of its own, so without this field §17's profile-freshness stamp
 * could cover the roster and not the shape — a profile that reads "updated today" while the shape
 * in it is a season old (pre-mortem PM-1).
 */
export interface TeamShape {
  readonly outOfPossession: string;
  readonly inPossession: string;
  readonly transition: string;
  /** Epoch milliseconds of the last shape write. `0` means "never written". */
  readonly updatedAt: number;
}

/**
 * The field names of {@link TeamShape} as values.
 *
 * Exists so `settingsB.parseTeamShape` (B-26) can be written **exhaustive over a closed union of
 * the shape's field names** with its own coverage alias — pre-mortem PM-4's mechanism: a fifth
 * field added to `TeamShape` without a parse arm becomes a compile error rather than a field that
 * silently never round-trips.
 */
export const TEAM_SHAPE_FIELDS = [
  'outOfPossession',
  'inPossession',
  'transition',
  'updatedAt',
] as const satisfies readonly (keyof TeamShape)[];

/**
 * `satisfies` proves every listed value belongs to the union; it does **not** prove the list covers
 * it. These close the other direction (`bidirectional-analysis`) with no runtime footprint, exactly
 * as {@link SettingKeysCoverTheUnion} does for settings: a member added to the type but not to the
 * list makes the conditional resolve to `false`, which fails `extends true` and breaks the build.
 *
 * This is the mechanism behind §26's *"merging one is a **compile** failure"* — deleting or merging
 * an `AttributeKey` cannot be done quietly.
 */
export type PositionsCoverTheUnion = AssertTrue<
  [Exclude<Position, (typeof POSITIONS)[number]>] extends [never] ? true : false
>;
export type AttributeRatingsCoverTheUnion = AssertTrue<
  [Exclude<AttributeRating, (typeof ATTRIBUTE_RATINGS)[number]>] extends [never] ? true : false
>;
export type AttributesCoverTheUnion = AssertTrue<
  [Exclude<AttributeKey, (typeof ATTRIBUTES)[number]['key']>] extends [never] ? true : false
>;
export type AttributeGroupsCoverTheUnion = AssertTrue<
  [Exclude<AttributeGroup, (typeof ATTRIBUTES)[number]['group']>] extends [never] ? true : false
>;
export type TeamShapeFieldsCoverTheUnion = AssertTrue<
  [Exclude<keyof TeamShape, (typeof TEAM_SHAPE_FIELDS)[number]>] extends [never] ? true : false
>;

// ─────────────────────────────────────────────────────────────────────────────
// Build unit B-1 — the B data model (design spec §4.1): our roster, hypothesis
// annotations, and coach cards. Three plain-data rows in the same discipline as
// everything above; commands and facades over them are B-2/B-3's.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * B §4.1 — one player of **our own** team (the My Team roster, scenario 2).
 *
 * Deliberately not a `PlayerRow`: that table is opponent shirt-number stubs under ENV-11's
 * no-identity rule, keyed `&[teamId+number]`. Our roster is the user's own squad profile, feeds
 * the Coach Claude payload (§5.2), and belongs to no team row — profile-scoped, no cascade owner
 * (deleting a match or an opponent team never touches it).
 *
 * **Reshaped by §15.3 (build unit B-25b).** `label` and the free-text `attributes` string are
 * **removed, not deprecated in place** — and `attributes` is deliberately *renamed* to `ratings`,
 * so a pre-reshape archive row is **structurally** distinguishable from a post-reshape one. That
 * is what makes §15.6's import repair exact rather than heuristic.
 *
 * Ruling **R-1** governs the name: first names stay, in the record, in the archive **and on the
 * wire**. ENV-11's no-identity rule governs *opponent* players — third parties the user is
 * scouting — while his own squad is a population whose first names he already knows and uses.
 */
export interface OurPlayerRow {
  id: string;
  /**
   * §3.7.36 — required. The same 1–99 band as opponent stubs
   * ({@link SHIRT_NUMBER_MIN}/{@link SHIRT_NUMBER_MAX} in `config/constants.ts`), refused with the
   * **existing** `ERR_SHIRT_NUMBER_RANGE` rather than a re-minted twin.
   *
   * **Not unique, and not indexed.** A mid-season kit change would otherwise need a temporary
   * value, and a `ConstraintError` in the middle of an ordinary edit costs more than an ambiguous
   * referent — which R-1 dissolves anyway, since the wire carries `#7 Eden`.
   */
  jerseyNumber: number;
  /** §3.7.36 — required, non-empty. Replaces `label`. Ruling R-1 is why this field exists. */
  firstName: string;
  /** §3.7.36 — the position the coach most frequently plays them in. Required. */
  primaryPosition: Position;
  /**
   * §3.7.36 — the **EXECUTION FILTER, not a history**. Listing a position here is *permission* for
   * Coach Claude to move the player there. **`[]` means the player must not be moved at all** —
   * a value, never a blank. Deduplicated, and never contains {@link primaryPosition}.
   */
  secondaryPositions: Position[];
  /**
   * §3.7.37 — a **SPARSE** map. **An absent key means NOT ASSESSED.**
   *
   * There is deliberately no `| null` rating: absence is the only representation of unrated, so
   * "blanks transmitted as absence" (§3.7.39) is a property of the *type* rather than of a filter
   * someone may later delete. A map rather than an array of `{key, rating}` because an array
   * admits three illegal states the map cannot express — a `rating: null` entry, a duplicate key,
   * and an unknown key.
   */
  ratings: Partial<Record<AttributeKey, AttributeRating>>;
  /**
   * Whether the player is on the current active roster (§2.4 My Team's active toggle).
   * **Never indexed** — IndexedDB does not accept a boolean as a key (the U-1 lesson).
   */
  active: boolean;
  createdAt: number;
  updatedAt: number;
}

/**
 * B §4.1 — a free-text note attached to one hypothesis of one match (scenario 17:
 * "applies to #7, the sub doesn't do this"). Feeds the Coach Claude payload (§5.2).
 *
 * **Mutable rows — the `matchNotes` INV-14 precedent**, and NOT an `Observation`: it never
 * reaches a count (`replay`/`reconcile` take `Observation[]` only). Match-scoped: INV-8
 * cascade + archive entity.
 */
export interface AnnotationRow {
  id: string;
  matchId: string;
  hypothesisId: string;
  text: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * `CoachCard.context` — which break of the match the card belongs to.
 *
 * This is exactly the existing `ArtifactPhase` vocabulary ("when in the match the artifact
 * was authored" — `TalkPoint.phase` and `MatchNote.phase` carry it already), reused rather
 * than re-declared: a second `'halftime' | 'fullTime'` union would be a sibling copy of a
 * vocabulary the `ses-1` lint already guards through `ARTIFACT_PHASE`. The spec's
 * `context: 'halftime'|'fullTime'` (§4.1) is this type, by name.
 */
export type CoachCardContext = ArtifactPhase;

/** B §4.1 — a card is a draft until `FinalizeCoachCard`; finalized cards are immutable. */
export type CoachCardStatus = 'draft' | 'final';

/** Where one adjustment entry came from (§2.5 step 3). */
export type AdjustmentOrigin = 'ai' | 'manual';

/**
 * Per-entry review state (§2.5 step 3). Acceptance is a **distinct persisted fact** —
 * nothing downstream fires on the mere existence of an AI suggestion; only `'accepted'`
 * (or a manual entry) reaches a finalized card.
 */
export type AdjustmentStatus = 'suggested' | 'accepted' | 'rejected';

/**
 * One adjustment inside a coach card (§4.1). Lives inside `CoachCardRow.adjustments` —
 * a JSON column read whole, the `setPieces.markers` shape, deliberately **not**
 * per-adjustment rows with ranks (which would structurally mirror the frozen `talkPoints`
 * table — a resemblance §4.1 avoids by construction).
 */
export interface AdjustmentEntry {
  entryId: string;
  /** The adjustment as it will read on the card — Coach Claude's text, an edit of it, or the user's own. */
  text: string;
  phase: HypothesisPhase;
  /** The selection entries this adjustment derives from ("because —", §2.5 step 4). May be empty for manual entries. */
  sourceHypothesisIds: string[];
  origin: AdjustmentOrigin;
  status: AdjustmentStatus;
  /**
   * Coach Claude's original wording, retained when an AI suggestion is edited (§2.5 step 3).
   * `null` on manual entries and on unedited AI entries alike until an edit stores it.
   */
  aiOriginalText: string | null;
  createdAt: number;
}

/**
 * B §4.1 — the adjustments-workflow card for one (match, context).
 *
 * **≤1 card per match per context is enforced at the DB grain**: the unique Dexie index
 * `&[matchId+context]` (migration `v004`), not handler discipline — an invariant without a
 * mechanism is a claim. Match-scoped: INV-8 cascade + archive entity, with a declared
 * nested field order for `adjustments`.
 */
export interface CoachCardRow {
  id: string;
  matchId: string;
  context: CoachCardContext;
  status: CoachCardStatus;
  /** The Step-1 selection, persisted at interaction grain. Deliberately unbounded (scenario 23). */
  selectedHypothesisIds: string[];
  adjustments: AdjustmentEntry[];
  /** When the last successful generate committed its suggestions, or `null` if none ever did. */
  generatedAt: number | null;
  /** Stamped by `FinalizeCoachCard`; `null` while the card is a draft. */
  finalizedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/** SYN-4 — the two archive scopes. `scopeId` is a match id, or the profile singleton. */
export type OutboxKind = 'match' | 'profile';

/** What last enqueued the row. Coalescing keeps the latest (§11.1). */
export type OutboxTrigger =
  | 'halftime'
  | 'fullTime'
  | 'distillComplete'
  | 'profileChange'
  | 'manual'
  | 'resume';

/** Named access, so the two `MatchState`-shaped strings are never spelled bare outside `types/`. */
export const OUTBOX_TRIGGER = {
  halftime: 'halftime',
  fullTime: 'fullTime',
  distillComplete: 'distillComplete',
  profileChange: 'profileChange',
  manual: 'manual',
  resume: 'resume',
} as const satisfies Record<OutboxTrigger, OutboxTrigger>;

export type OutboxState = 'pending' | 'failed';

/**
 * §3.4 — `[kind+scopeId]` is the **primary key**, not a unique index over a surrogate id.
 *
 * `put` upserts by primary key, so re-triggering coalesces (SYN-10). With a surrogate id, a fresh
 * ULID against a merely-unique `&[kind+scopeId]` throws `ConstraintError` — and because the outbox
 * row shares the state transition's transaction, that would abort the Half or Full Time transition
 * itself whenever a prior push had not drained. Do not "normalize" this.
 */
export interface OutboxRow {
  kind: OutboxKind;
  scopeId: string;
  trigger: OutboxTrigger;
  enqueuedAt: number;
  attempts: number;
  lastAttemptAt: number | null;
  lastError: string | null;
  state: OutboxState;
}

/**
 * The id every singleton row carries. Design §1.4: session configuration is a tiny record, so a UI
 * refactor is never a Dexie migration.
 */
export const SINGLETON_ID = 'singleton';

/** SES-3 / §1.4 — which screen the device was on. Not domain data; excluded from VER-7 (§17.4). */
export interface UiSessionRow {
  id: typeof SINGLETON_ID;
  matchId: string | null;
  surface: string;
  surfaceParams: Record<string, unknown>;
  updatedAt: number;
}

/** §13.7 — the single-writer advisory lock. Excluded from VER-7: it identifies a browsing context. */
export interface ActiveMatchRow {
  id: typeof SINGLETON_ID;
  matchId: string | null;
  writerSessionId: string;
  heartbeatTs: number;
}

/**
 * BAK-5 settings plus `lastSeenVersion`.
 *
 * `backupCredential` lives here and is on `archive/fields.ts`'s deny-list (§11.8) — it is excluded
 * from the BAK-1 export because that file gets shared, and from the SYN-4 profile archive because
 * pushing a credential to the service it authenticates is circular.
 *
 * **The key space is CLOSED, and that is the mechanism behind §11.8's exclusion.** A deny-list
 * over an open `string` key is a list somebody has to remember to update; declaring the union here
 * lets `archive/fields.ts` write `satisfies Record<SettingKey, ...>`, so **adding a setting does
 * not compile until its publish-or-deny disposition is declared**. Build unit U-5 narrowed this
 * field from `string` for exactly that reason.
 *
 * `themePreference` (B spec §4.2, F-5's package — build unit B-4) is B's light/dark choice.
 * **Device-local by declaration**: a theme is a property of the device's viewing conditions, not
 * of the data set, so it joins `backupCredential` on the deny-list — for a different reason,
 * recorded at its `SETTING_DISPOSITION` entry.
 *
 * `teamShape` (B spec §15.4, ruling **R-4** — build unit B-25) is the team's preferred shape, a
 * structured {@link TeamShape} value. **One key, not three**, and that is a correctness argument
 * rather than an aesthetic one: the bus opens exactly one transaction per command, so three keys
 * would be three dispatches with no atomicity across them, and an iOS kill between two of them
 * would leave a profile whose in-possession shape saved and whose out-of-possession shape did not.
 * One key = one `UpdateSetting` = one transaction = the whole shape commits or none of it does.
 */
export type SettingKey =
  | 'lockNudgeMin'
  | 'audioTickOn'
  | 'backupCredential'
  | 'lastSeenVersion'
  | 'themePreference'
  | 'teamShape';

/**
 * The six keys as values, and as the runtime list the repo-boundary validator parses against —
 * and, since B-4, the list `handleUpdateSetting` refuses unknown keys against (`ERR_SETTING_KEY`).
 *
 * Ordered as declared, not sorted: this is a vocabulary, and `SETTING_KEYS` is never itself
 * serialized — canonical ordering is `archive/`'s business, applied to rows.
 *
 * **The category strain is named rather than hidden** (R-4's stated cost): this reads as four
 * device preferences, a credential, and a tactical profile. The boundary rule is that `settings`
 * holds profile-scoped **singleton** values and anything with more than one row is a table — a
 * comment, not a mechanism, and recorded here so the next person adding a key knows the test.
 */
export const SETTING_KEYS = [
  'lockNudgeMin',
  'audioTickOn',
  'backupCredential',
  'lastSeenVersion',
  'themePreference',
  'teamShape',
] as const satisfies readonly SettingKey[];

/**
 * `satisfies` proves every listed key is a `SettingKey`; it does **not** prove the list covers the
 * union. This alias closes the other direction (`bidirectional-analysis`) with no runtime
 * footprint: a key added to `SettingKey` but not to `SETTING_KEYS` makes the conditional resolve to
 * `false`, which fails the `extends true` constraint and breaks the build.
 */
type AssertTrue<T extends true> = T;
export type SettingKeysCoverTheUnion = AssertTrue<
  [Exclude<SettingKey, (typeof SETTING_KEYS)[number]>] extends [never] ? true : false
>;

export interface SettingRow {
  key: SettingKey;
  value: unknown;
}

/** OBS-1 — capped ring buffer of ~2,000 entries in IndexedDB in production. */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * A log entry as the **pure** layer produces it — design §1.1's `Effect.log`.
 *
 * No `ts`: a pure function may not read a clock. The logger stamps the timestamp when it accepts
 * the entry. This exists so SES-1's "rejection is observable — the domain layer returns an error
 * **and the log records it**" can be discharged inside `domain/`, rather than being a promise that
 * some later caller remembers to keep. `domain/result.ts` makes it structural: a rejection cannot
 * be constructed without one.
 */
export interface DomainLogEntry {
  module: string;
  level: LogLevel;
  matchId: string | null;
  msg: string;
  data: Record<string, unknown>;
}

export interface LogEntryRow {
  /** Auto-incrementing; the ring is trimmed from the low end. */
  seq?: number;
  ts: number;
  level: LogLevel;
  /** Module category — OBS-1. */
  module: string;
  /** Correlation id. OBS-1 names `matchId`; U-0 correlates by harness run id in the same field. */
  matchId: string | null;
  msg: string;
  data: Record<string, unknown> | null;
}

// NOTE: the bulk-blob row deliberately does NOT live here. It carries a `Blob`, which is a DOM
// type, and `types/` is compiled by `tsconfig.pure.json` with `lib: ["ES2022"]`. Declaring it here
// would silently make every pure layer DOM-dependent. It lives in `data/db.ts` instead.

// ─────────────────────────────────────────────────────────────────────────────
// Harness result rows
// ─────────────────────────────────────────────────────────────────────────────

/** The three conditions §16 U-0 names, plus the human-driven variant. */
export type RunCondition = 'warm' | 'cold' | 'thermal';

export type RunMode = 'auto' | 'liveTap';

/**
 * One measured two-row `readwrite`.
 *
 * The three legs are separated because NFR-1's budget covers all of them and A-1 covers only the
 * middle one. Reporting a single number would make it impossible to tell a slow IndexedDB from a
 * slow frame boundary — and those have different remedies.
 */
export interface TrialSample {
  index: number;
  /** `pointerup → dispatch` (liveTap only; 0 in auto mode, where there is no touch). */
  inputMs: number;
  /** `dispatch → IDBTransaction 'complete'`. **This is the A-1 number.** */
  commitMs: number;
  /** `'complete' → the next animation frame`, i.e. when the LIVE-2 flash can first paint. */
  paintMs: number;
  /** `anchor → next animation frame` — the full NFR-1 tap-to-ack-begins latency. */
  totalMs: number;
  /** Milliseconds since the page was loaded. Lets a genuinely-cold first trial be identified. */
  sinceLoadMs: number;
}

export interface StorageProbe {
  supported: boolean;
  usageBytes: number | null;
  quotaBytes: number | null;
  usageFraction: number | null;
  /** `navigator.storage.persisted()` before any request. */
  persistedBefore: boolean | null;
  /** `navigator.storage.persist()` result, when the run requested it. `null` = not requested. */
  persistRequested: boolean | null;
  persistedAfter: boolean | null;
  /** Non-standard breakdown Safari may or may not provide. Recorded verbatim when present. */
  breakdown: Record<string, number> | null;
  error: string | null;
}

export interface EnvironmentReport {
  /**
   * The bundle that produced this measurement (`YYYYMMDD-HHMM-sha[-dirty]`).
   *
   * An exported result is evidence, and evidence that cannot name its own instrument version is
   * weaker than it looks — a fix and a stale bundle otherwise produce indistinguishable files.
   * Runs recorded before this field existed read as `'unknown'`; see `resultsStore.loadRuns`.
   */
  buildId: string;
  userAgent: string;
  /** True when running as an installed home-screen PWA. ENV-1's gate. */
  standalone: boolean;
  /** Best-effort iOS Safari detection. Used only to label the run, never to branch behavior. */
  looksLikeIosSafari: boolean;
  screenW: number;
  screenH: number;
  viewportW: number;
  viewportH: number;
  devicePixelRatio: number;
  hardwareConcurrency: number | null;
  /** Measured, not assumed: Safari clamps `performance.now()`, so the floor on every figure below. */
  timerResolutionMs: number;
  language: string;
  timeZone: string;
  capturedAt: number;
}

export interface SeedProfile {
  matches: number;
  observationsPerMatch: number;
  hypothesesPerMatch: number;
  observationRows: number;
  hypothesisRows: number;
  bulkBlobBytes: number;
}

export interface RunRecord {
  id: string;
  startedAt: number;
  finishedAt: number;
  condition: RunCondition;
  mode: RunMode;
  /** Seconds of sustained multi-core load completed before measurement (thermal condition only). */
  soakCompletedS: number;
  /** Workers actually spawned for the thermal condition. */
  heatWorkers: number;
  /**
   * Peak number of heat workers belonging to a **different** run that were burning CPU during this
   * run's sampling window, or `null` when the run predates contamination detection.
   *
   * ── The tri-state is the point ──────────────────────────────────────────────
   * `0` means *checked, and clean*. `null` means *not checked* — and those must never read the
   * same. A run recorded before this field existed cannot attest that it was clean, and the one
   * that prompted the field was in fact measured under eight heat workers on a four-core device
   * and published a false `breached` finding. Defaulting a missing value to `0` would let exactly
   * that run keep voting on the aggregate verdict for ever.
   *
   * **Absence of a provenance field is not evidence of good provenance.** `scenario.ts` therefore
   * treats `null` as an admissibility blocker in its own right, distinct from contamination, and
   * says so on screen rather than quietly dropping the run.
   *
   * Normalised at the one boundary where legacy rows enter — `resultsStore.loadRuns`.
   */
  foreignHeatWorkersDuringRun: number | null;
  warmupTrials: number;
  samples: TrialSample[];
  seed: SeedProfile;
  storage: StorageProbe;
  environment: EnvironmentReport;
  /** Design §12.1 — proof the logger did not write inside the measured window. */
  logFlushesDuringRun: number;
  /** Any trial that threw. Never silently dropped (NFR-8). */
  failures: { index: number; message: string }[];
  notes: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build unit U-1 — MediaRecorder spike result rows (assumption A-2)
//
// NOTE: none of these carries a `Blob`. The chunks and the assembled segments do, and they live in
// `data/audioSpikeStore.ts` — the same rule that keeps `BulkBlobRow` out of this file. A `Blob`
// here would make every pure layer DOM-dependent and silently disable the NFR-6 boundary that
// `tsconfig.pure.json` enforces. (Design §1.2's "no imports" for `types/` vs §3.1's `AudioBlob`
// (blob) — resolved in favour of `data/`.)
// ─────────────────────────────────────────────────────────────────────────────

/** One row of the AUD-1 "what does this browser actually emit" matrix. */
export interface MimeSupport {
  mime: string;
  supported: boolean;
}

export interface AudioCapabilityReport {
  mediaRecorderSupported: boolean;
  getUserMediaSupported: boolean;
  /** `navigator.permissions.query({name:'microphone'})`, when the browser implements it. */
  permissionState: 'granted' | 'denied' | 'prompt' | 'unsupported' | 'error';
  supportMatrix: MimeSupport[];
  /**
   * What `new MediaRecorder(stream).mimeType` reports with **no** `mimeType` option — the container
   * the browser picks for itself. AUD-1 stores what the recorder emitted, so this is the value the
   * product will be carrying, not one we chose.
   */
  defaultMime: string | null;
  probedAt: number;
}

/** Which acquisition path a trial measured. */
export type GumKind = 'firstEver' | 'cold' | 'warm';

/**
 * One `getUserMedia` acquisition, or one tap→recording start on an already-held stream.
 *
 * FLAG-1 requires a single tap to start recording *immediately*. The number that matters is not
 * `getUserMedia` in isolation but **anchor → the recorder is actually running**, because that is
 * the interval in which the opening words of a flag are lost.
 */
export interface GumTrial {
  index: number;
  kind: GumKind;
  /** `getUserMedia` call → promise resolved. `0` on the warm path, where no call is made. */
  acquireMs: number;
  /** `new MediaRecorder(...).start()` → the recorder's `start` event. */
  recorderStartMs: number;
  /** anchor → recorder running. The FLAG-1 figure. */
  tapToRecordingMs: number;
  trackLabel: string | null;
  /** Populated instead of the timings when acquisition failed (AUD-4 denial included). */
  error: string | null;
}

/** One `dataavailable` payload, as it arrived and as it was persisted. */
export interface ChunkStat {
  index: number;
  /** Milliseconds from `recorder.start()` to this `dataavailable`. */
  sinceStartMs: number;
  /** Milliseconds since the previous chunk. `null` for the first. The cadence §8.5 depends on. */
  sinceLastMs: number | null;
  sizeBytes: number;
  /** `Blob.type` on this chunk. iOS may leave it empty on all but the first — reported, not assumed. */
  type: string;
  /** Time to write this chunk to its own IndexedDB transaction (design §8.2 step 2). */
  persistMs: number;
  /** Set when the chunk arrived but could not be persisted. Never silently dropped (NFR-8). */
  persistError: string | null;
}

/**
 * Which slice of the chunk sequence a playability probe was run against.
 *
 * `full` and `truncatedTail` are the two the design depends on. `withoutFirstChunk` is a **negative
 * control**: A-2 asserts the init fragment must come first, and if a sequence *without* it is
 * equally playable then that part of the assumption is not a constraint on this browser.
 */
export type AssemblyVariant = 'full' | 'truncatedTail' | 'firstChunkOnly' | 'withoutFirstChunk';

/**
 * The result of assembling a chunk slice verbatim and asking the browser to play it.
 *
 * `canPlayThrough` is the strict signal and the only one the A-2 verdict uses. `metadataLoaded` +
 * `durationSeconds` are reported separately because a fragmented MP4 missing its final fragment can
 * parse far enough to yield metadata and still refuse to play through — and telling those two
 * outcomes apart is the difference between "the container is broken" and "the browser would not
 * commit". `manualVerdict` on the segment is the human tiebreak.
 */
export interface PlayabilityResult {
  variant: AssemblyVariant;
  chunkCount: number;
  sizeBytes: number;
  mime: string;
  canPlayThrough: boolean;
  metadataLoaded: boolean;
  durationSeconds: number | null;
  /** `HTMLMediaElement.error.code`, when the element reported one. */
  mediaErrorCode: number | null;
  mediaErrorMessage: string | null;
  probeMs: number;
  timedOut: boolean;
  /** Populated when the probe itself could not run (no `Audio` constructor, object URL refused). */
  error: string | null;
}

/**
 * What ended the recording. AUD-3 requires that **every** one of these routes through the same
 * finalize path, so the trigger is recorded per segment and the report checks the set was covered.
 */
export type FinalizeTrigger =
  | 'userStop'
  | 'plannedDuration'
  | 'voiceNoteMax'
  | 'recorderError'
  | 'trackEnded'
  | 'pageHide'
  | 'visibilityHidden'
  | 'recovered';

/** What Andrew heard when he pressed play. The only acceptance test AUD-1 actually has. */
export type ManualVerdict = 'unset' | 'played' | 'silent' | 'refused';

export interface SegmentRecord {
  id: string;
  startedAt: number;
  /** Free label so a venue run can distinguish "phone call test" from "background test". */
  label: string;
  /** SES-10 — derived at recording START, never at finalize. Recorded to prove the ordering held. */
  matchMinuteAtStart: number;
  trigger: FinalizeTrigger | null;
  /** The `mimeType` requested of the constructor, or `null` when the browser chose. */
  requestedMime: string | null;
  /** `recorder.mimeType` — what AUD-1 requires be stored with the blob. */
  actualMime: string | null;
  chunks: ChunkStat[];
  totalBytes: number;
  /** `recorder.start()` → finalize resolved. */
  wallDurationMs: number;
  /** False for a segment recovered with `complete=false` (design §8.5's `pageshow` pass). */
  complete: boolean;
  probes: PlayabilityResult[];
  /**
   * AUD-3: "the recorder is never left in a state where a subsequent record attempt fails."
   * Populated by attempting a fresh short recording immediately after this one finalized.
   */
  recorderReusable: { attempted: boolean; succeeded: boolean; message: string | null };
  manualVerdict: ManualVerdict;
  notes: string | null;
}

export interface AudioRunRecord {
  id: string;
  startedAt: number;
  finishedAt: number;
  capabilities: AudioCapabilityReport;
  gumTrials: GumTrial[];
  segments: SegmentRecord[];
  environment: EnvironmentReport;
  storage: StorageProbe;
  /** Anything that threw outside a trial. Never silently dropped (NFR-8). */
  failures: { stage: string; message: string }[];
  notes: string | null;
}
